import React, { useMemo, useState } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { ProductCard } from '../components/product/ProductCard';
import { TrustBadge } from '../components/ui/TrustBadge';
import { mockProducts, categories } from '../data/mockProducts';
export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialFilter = searchParams.get('filter') || '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showWICOnly, setShowWICOnly] = useState(initialFilter === 'wic');
  const [showEBTOnly, setShowEBTOnly] = useState(initialFilter === 'ebt');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filteredProducts = useMemo(() => {
    let results = mockProducts;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      results = results.filter(p => p.name.toLowerCase().includes(query) || p.category.toLowerCase().includes(query));
    }
    if (selectedCategory !== 'All') {
      results = results.filter(p => p.category === selectedCategory);
    }
    if (showWICOnly) {
      results = results.filter(p => p.isWIC);
    }
    if (showEBTOnly) {
      results = results.filter(p => p.isEBT);
    }
    return results;
  }, [searchQuery, selectedCategory, showWICOnly, showEBTOnly]);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({
        q: searchQuery
      });
    } else {
      setSearchParams({});
    }
  };
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setShowWICOnly(false);
    setShowEBTOnly(false);
    setSearchParams({});
  };
  const hasActiveFilters = searchQuery || selectedCategory !== 'All' || showWICOnly || showEBTOnly;
  return <div className="min-h-screen bg-[#FAF8F5] pb-20 md:pb-0" data-id="element-357">
      <TopNav data-id="element-358" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8" data-id="element-359">
        {/* Search Header */}
        <div className="mb-6" data-id="element-360">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-361">
            Shop All Products
          </h1>

          {/* Desktop Search & Filters */}
          <div className="hidden md:flex items-center gap-4" data-id="element-362">
            <form onSubmit={handleSearch} className="flex-1 relative" data-id="element-363">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-364" />
              <input type="text" placeholder="Search for products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-12 pr-4 text-base focus:ring-2 focus:ring-[#948B7A] focus:border-transparent transition-all" data-id="element-365" />
            </form>

            <div className="flex items-center gap-2" data-id="element-366">
              <button onClick={() => setShowWICOnly(!showWICOnly)} className={`px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${showWICOnly ? 'bg-[#047857] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-id="element-367">
                WIC Only
              </button>
              <button onClick={() => setShowEBTOnly(!showEBTOnly)} className={`px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${showEBTOnly ? 'bg-[#1E3A5F] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-id="element-368">
                EBT Only
              </button>
            </div>
          </div>

          {/* Mobile Filter Button */}
          <div className="md:hidden" data-id="element-369">
            <button onClick={() => setIsFilterOpen(true)} className="flex items-center gap-2 bg-white border border-gray-200 px-4 py-2.5 rounded-xl font-semibold text-sm text-gray-700" data-id="element-370">
              <Filter className="w-4 h-4" data-id="element-371" />
              Filters
              {hasActiveFilters && <span className="bg-[#948B7A] text-white text-xs px-1.5 py-0.5 rounded-full" data-id="element-372">
                  Active
                </span>}
            </button>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide" data-id="element-373">
          {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-full font-medium text-sm whitespace-nowrap transition-colors ${selectedCategory === category ? 'bg-[#948B7A] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-id="element-374">
              {category}
            </button>)}
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && <div className="flex items-center gap-2 mb-6 flex-wrap" data-id="element-375">
            <span className="text-sm text-gray-500" data-id="element-376">Active filters:</span>
            {searchQuery && <span className="inline-flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full text-sm" data-id="element-377">
                "{searchQuery}"
                <button onClick={() => setSearchQuery('')} data-id="element-378">
                  <X className="w-3 h-3" data-id="element-379" />
                </button>
              </span>}
            {selectedCategory !== 'All' && <span className="inline-flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full text-sm" data-id="element-380">
                {selectedCategory}
                <button onClick={() => setSelectedCategory('All')} data-id="element-381">
                  <X className="w-3 h-3" data-id="element-382" />
                </button>
              </span>}
            {showWICOnly && <span className="inline-flex items-center gap-1" data-id="element-383">
                <TrustBadge type="WIC" size="sm" data-id="element-384" />
                <button onClick={() => setShowWICOnly(false)} data-id="element-385">
                  <X className="w-3 h-3 text-gray-500" data-id="element-386" />
                </button>
              </span>}
            {showEBTOnly && <span className="inline-flex items-center gap-1" data-id="element-387">
                <TrustBadge type="EBT" size="sm" data-id="element-388" />
                <button onClick={() => setShowEBTOnly(false)} data-id="element-389">
                  <X className="w-3 h-3 text-gray-500" data-id="element-390" />
                </button>
              </span>}
            <button onClick={clearFilters} className="text-sm text-[#D97757] font-medium hover:underline" data-id="element-391">
              Clear all
            </button>
          </div>}

        {/* Results Count */}
        <p className="text-sm text-gray-500 mb-6" data-id="element-392">
          Showing {filteredProducts.length} product
          {filteredProducts.length !== 1 ? 's' : ''}
        </p>

        {/* Product Grid */}
        {filteredProducts.length > 0 ? <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-393">
            {filteredProducts.map((product, index) => <motion.div key={product.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.03
        }} data-id="element-394">
                <ProductCard product={product} data-id="element-395" />
              </motion.div>)}
          </div> : <div className="text-center py-16" data-id="element-396">
            <div className="text-6xl mb-4" data-id="element-397">🔍</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-398">
              No products found
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-399">
              Try adjusting your search or filters to find what you're looking
              for.
            </p>
            <button onClick={clearFilters} className="bg-[#948B7A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#7d7465] transition-colors" data-id="element-400">
              Clear All Filters
            </button>
          </div>}
      </div>

      {/* Mobile Filter Drawer */}
      <AnimatePresence data-id="element-401">
        {isFilterOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsFilterOpen(false)} className="fixed inset-0 bg-black/50 z-40 md:hidden" data-id="element-402" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 md:hidden max-h-[80vh] overflow-y-auto" data-id="element-403">
              <div className="p-6" data-id="element-404">
                <div className="flex items-center justify-between mb-6" data-id="element-405">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-406">Filters</h3>
                  <button onClick={() => setIsFilterOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg" data-id="element-407">
                    <X className="w-6 h-6" data-id="element-408" />
                  </button>
                </div>

                {/* Search */}
                <div className="mb-6" data-id="element-409">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-410">
                    Search
                  </label>
                  <div className="relative" data-id="element-411">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-412" />
                    <input type="text" placeholder="Search products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-sm" data-id="element-413" />
                  </div>
                </div>

                {/* Benefits Filter */}
                <div className="mb-6" data-id="element-414">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-415">
                    Benefits Accepted
                  </label>
                  <div className="flex gap-3" data-id="element-416">
                    <button onClick={() => setShowWICOnly(!showWICOnly)} className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors ${showWICOnly ? 'bg-[#047857] text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-417">
                      WIC Only
                    </button>
                    <button onClick={() => setShowEBTOnly(!showEBTOnly)} className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors ${showEBTOnly ? 'bg-[#1E3A5F] text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-418">
                      EBT Only
                    </button>
                  </div>
                </div>

                {/* Category */}
                <div className="mb-6" data-id="element-419">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-420">
                    Category
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-421">
                    {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${selectedCategory === category ? 'bg-[#948B7A] text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-422">
                        {category}
                      </button>)}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200" data-id="element-423">
                  <button onClick={clearFilters} className="flex-1 py-3 rounded-xl font-semibold text-sm bg-gray-100 text-gray-700" data-id="element-424">
                    Clear All
                  </button>
                  <button onClick={() => setIsFilterOpen(false)} className="flex-1 py-3 rounded-xl font-semibold text-sm bg-[#948B7A] text-white" data-id="element-425">
                    Show Results
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      <BottomNav data-id="element-426" />
    </div>;
}